import { RouterProvider as AriaRouterProvider } from 'react-aria-components';
import { Outlet, useHref, useNavigate } from 'react-router-dom';

import EmailThreadPanel from '@/components/proposals/EmailThreadPanel';
import ProposalSidebar from '@/components/proposals/ProposalSidebar';
import { GlobalSearch } from '@/components/search/GlobalSearch';
import {
  ProposalSidebarProvider,
  useProposalSidebar,
} from '@/contexts/ActionSidebarContext';
import { EmailComposeProvider } from '@/contexts/EmailComposeContext';
import { MemoComposeProvider } from '@/contexts/MemoComposeContext';
import { OverlayBackdropProvider } from '@/contexts/OverlayBackdropContext';
import { OverlayOrderProvider } from '@/contexts/OverlayOrderContext';
import { ProposalViewHostProvider } from '@/contexts/ProposalViewHostContext';
import { SlideoutWidthProvider } from '@/contexts/SlideoutWidthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { EmailComposeRenderer } from '@/lib/compose/EmailComposePanel';
import { SlideoutStackProvider } from '@/lib/crm/contexts/SlideoutStackContext';
import { CreateSlideoutProvider } from '@/lib/crm/create/CreateSlideoutProvider';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { CrmObjectSlideout } from '@/lib/crm/record';
import { MemoComposeRenderer } from '@/lib/crm/record/notes/MemoComposePanel';
import { cx } from '@/utils/cx';

/**
 * Persistent dim-the-page overlay rendered behind the slideout panels.
 *
 * Lives at the renderer level rather than inside each slideout component so
 * it doesn't unmount when the topmost slideout transitions between kinds
 * (e.g. proposal → entity layered on top → proposal again). The fade is
 * driven by `visible` and animates independently of the slideout panel
 * itself.
 *
 * z-index sits between the page (BASE_Z_INDEX = 400) and the first
 * registered slideout overlay (>= 600 — `useOverlayOrder` always returns
 * level >= 2 for an active slideout, and `useStackingZIndex` is
 * `400 + level * 100`). Hardcoded to keep the rule self-contained: this
 * backdrop is intentionally OUTSIDE the StackingLevel system so it
 * doesn't bump the counter when slideouts mount/unmount.
 */
function SlideoutBackdrop({ visible }: { visible: boolean }) {
  return (
    <div
      aria-hidden='true'
      className={cx(
        'fixed inset-0 z-[500] bg-black/50 transition-opacity duration-300 ease-out',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0'
      )}
    />
  );
}

/**
 * Renders ONE slideout component at a time — the topmost entry in the
 * unified slideout stack. The stack itself still tracks the full history
 * (URL-synced via `slideoutEntityId`, back-navigation works), but only
 * one slideout panel exists in the DOM at any moment. Closing the top
 * entry pops it off the stack and mounts the next entry's renderer.
 *
 * The dim backdrop is rendered ONCE here (not per slideout) so it
 * persists across kind transitions — opening an entity on top of a
 * proposal (or an email thread) keeps the page dimmed, instead of
 * flashing un-dimmed for the duration of the swap.
 *
 * Adding a new slideout kind: extend the `SlideoutKind` type in
 * `SlideoutStackContext`, add a case here, and the renderer will start
 * dispatching to it. If the new kind should keep the page dimmed,
 * extend `KINDS_WITH_BACKDROP` below. An entry whose kind is claimed by
 * an inline host never gets a backdrop, regardless of membership here —
 * it renders as a real page sibling with nothing to dim behind it.
 */
const KINDS_WITH_BACKDROP = new Set<string>(['proposal', 'email_thread']);

// Named export for tests only — Root is the sole production mount point.
export function SlideoutRenderer() {
  const { stack, topEntry, inlineHostKinds } = useSlideoutStack();
  const { isSidebarVisible } = useProposalSidebar();

  // Backdrop is shown when any entry in the stack opts into a backdrop.
  //
  // Entries claimed by an inline host (e.g. the chat page's docked
  // proposal pane) render as a real sibling of the page layout, not a
  // floating overlay — there is nothing to dim behind them, so they are
  // excluded from both scans below.
  //
  // Proposals coordinate with `isSidebarVisible` so the backdrop fades
  // out alongside the 500ms exit animation (the flag flips false at the
  // start of close, before the stack actually pops). Other backdrop-
  // enabled kinds pop immediately on close, so if any non-proposal
  // backdrop kind is in the stack, keep the backdrop up regardless of
  // the proposal's animating visibility flag — otherwise an email-thread
  // layered above a closing proposal would briefly lose its dim.
  const backdropEligible = stack.filter(e => !inlineHostKinds.has(e.kind));
  const stackWantsBackdrop = backdropEligible.some(e =>
    KINDS_WITH_BACKDROP.has(e.kind)
  );
  const hasNonProposalBackdropKind = backdropEligible.some(
    e => KINDS_WITH_BACKDROP.has(e.kind) && e.kind !== 'proposal'
  );
  const backdropVisible =
    stackWantsBackdrop && (isSidebarVisible || hasNonProposalBackdropKind);

  return (
    <>
      <SlideoutBackdrop visible={backdropVisible} />
      {/* A kind claimed by an inline host (e.g. the action detail page's
          SplitView entity panel) renders inside that page's own layout — the
          global overlay stands down for it. */}
      {topEntry &&
        !inlineHostKinds.has(topEntry.kind) &&
        (() => {
          switch (topEntry.kind) {
            case 'entity':
              return <CrmObjectSlideout entry={topEntry} />;
            case 'proposal':
              return <ProposalSidebar entry={topEntry} />;
            case 'email_thread':
              return <EmailThreadPanel entry={topEntry} />;
          }
        })()}
    </>
  );
}

export default function Root() {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  return (
    <AriaRouterProvider navigate={navigate} useHref={useHref}>
      <OverlayBackdropProvider>
        <OverlayOrderProvider>
          <SlideoutStackProvider>
            <ProposalSidebarProvider>
              {/* Inside SlideoutStackProvider: create forms open over record
                  slideouts and push records (conflict links, onCreated). */}
              <CreateSlideoutProvider>
                <MemoComposeProvider>
                  <EmailComposeProvider>
                    <SlideoutWidthProvider>
                      {/* Above BOTH the Outlet and SlideoutRenderer so a page
                          hosting a proposal (ActionDetailPage) can signal it to
                          the record surface whether that surface renders inline
                          in the page or via the global overlay fallback. */}
                      <ProposalViewHostProvider>
                        <main className={`h-full ${isDark ? 'dark-mode' : ''}`}>
                          <Outlet />
                        </main>
                        <SlideoutRenderer />
                        <MemoComposeRenderer />
                        <EmailComposeRenderer />
                        <GlobalSearch />
                      </ProposalViewHostProvider>
                    </SlideoutWidthProvider>
                  </EmailComposeProvider>
                </MemoComposeProvider>
              </CreateSlideoutProvider>
            </ProposalSidebarProvider>
          </SlideoutStackProvider>
        </OverlayOrderProvider>
      </OverlayBackdropProvider>
    </AriaRouterProvider>
  );
}
